import React, { useState } from 'react';

import classNames from 'classnames';

import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';
import { faChevronDown, faChevronRight, faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { pluralize } from 'helpers/formatters/string_formats';

import type { AssistantQuestion } from './types';

interface Props {
  answers: (string | null)[] | null;
  note: string | null;
  questions: AssistantQuestion[];
}

// Stands in for the card once it can no longer be answered, so the transcript still shows what was
// asked instead of a gap where a question used to be.
export function QuestionSummary({ answers, note, questions }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger
        className="btn btn-link btn-sm d-flex align-items-center gap-2 p-0 text-muted text-decoration-none"
      >
        {getHeadline(answers, note, questions.length)}
        <FontAwesomeIcon className="fs-xs" icon={open ? faChevronDown : faChevronRight} />
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div className="d-flex flex-column gap-2 small mt-2">
          {questions.map((question, index) => (
            <React.Fragment key={question.question}>
              {index > 0 && <Separator />}
              <div className="d-flex flex-column gap-1">
                <div className="d-flex align-items-center gap-2">
                  {answers?.[index]
                    ? <FontAwesomeIcon className="text-success flex-shrink-0" icon={faCircleCheck} />
                    : <FontAwesomeIcon className="text-muted flex-shrink-0" icon={faCircle} />}
                  <span className="text-muted">{question.question}</span>
                </div>
                {answers && (
                  <div className={classNames('ps-5', answers[index] ? 'fw-semibold' : 'text-muted fst-italic')}>
                    {answers[index] || 'Skipped'}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </Collapsible.Content>
    </Collapsible>
  );
}

function getHeadline(answers: (string | null)[] | null, note: string | null, asked: number) {
  if (!answers) {
    return note;
  }

  const answered = answers.filter(Boolean).length;

  return answered === asked
    ? `${pluralize(answered, 'question')} answered`
    : `${answered} of ${pluralize(asked, 'question')} answered`;
}
